
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from '@/hooks/useAuth';
import { useRoleGuard } from '@/hooks/use-role-guard';
import AdminUsersTab from '@/components/admin/AdminUsersTab';
import AdminStudiosTab from '@/components/admin/AdminStudiosTab';
import AdminStatsTab from '@/components/admin/AdminStatsTab';
import AdminSubscriptionsTab from '@/components/admin/AdminSubscriptionsTab';
import AdminSupportTab from '@/components/admin/AdminSupportTab';
import AdminPartnerApplicationsTab from '@/components/admin/AdminPartnerApplicationsTab';
import AdminReferralsTab from '@/components/admin/AdminReferralsTab';
import AdminDashboardLinks from '@/components/admin/AdminDashboardLinks';
import { Loader2, AlertTriangle } from 'lucide-react';
import { DemoAccountBanner } from '@/components/ui/demo-badge';

const AdminDashboard = () => {
  // Use the role guard hook to protect this page
  useRoleGuard(['admin']);
  
  const { isAdmin, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("users");

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
          <p className="text-muted-foreground">Lädt Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Card className="max-w-3xl mx-auto mt-8">
        <CardHeader>
          <CardTitle className="text-red-500 flex items-center gap-2">
            <AlertTriangle size={24} />
            Zugriff verweigert
          </CardTitle>
          <CardDescription>
            Du hast keine Berechtigung, auf das Admin-Dashboard zuzugreifen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p>Dieses Dashboard ist nur für Administratoren zugänglich.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="container py-6">
      {/* Demo account banner will only show for demo accounts */}
      <DemoAccountBanner />

      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>
      
      {/* Add the AdminDashboardLinks component */}
      <div className="mb-6">
        <AdminDashboardLinks />
      </div>
      
      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 mb-6 w-full">
          <TabsTrigger value="users">Nutzer</TabsTrigger>
          <TabsTrigger value="studios">Studios</TabsTrigger>
          <TabsTrigger value="stats">Statistiken</TabsTrigger>
          <TabsTrigger value="subscriptions">Abos</TabsTrigger>
          <TabsTrigger value="partners">Partner</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          <TabsTrigger value="support">Support & Logs</TabsTrigger>
        </TabsList>
        
        <TabsContent value="users">
          <AdminUsersTab />
        </TabsContent>
        
        <TabsContent value="studios">
          <AdminStudiosTab />
        </TabsContent>
        
        <TabsContent value="stats">
          <AdminStatsTab />
        </TabsContent>
        
        <TabsContent value="subscriptions">
          <AdminSubscriptionsTab />
        </TabsContent>
        
        <TabsContent value="partners">
          <AdminPartnerApplicationsTab />
        </TabsContent>
        
        <TabsContent value="referrals">
          <AdminReferralsTab />
        </TabsContent>
        
        <TabsContent value="support">
          <AdminSupportTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
